<script setup lang="ts">
/**
 * DM-Ansicht: Header mit @User + Status, Nachrichtenliste (Slot), Eingabezeile.
 * Layout, Farben und Abstände nach Discord Dark Theme.
 */
withDefaults(defineProps<{
  user: string
  avatarColor?: string
  /** DM mit dem Bot: Header-Avatar = Clarify-Kachel (wie in Discord) */
  bot?: boolean
  placeholder?: string
}>(), {
  avatarColor: '#e8a33d',
  bot: false,
  placeholder: 'Message @user'
})
</script>

<template>
  <div class="dc-dm">
    <div class="dc-dm-header">
      <DiscordAvatar :name="user" :bot="bot" :color="avatarColor" :size="24" status="online" />
      <span class="dc-dm-header-name">{{ user }}</span>
      <div class="dc-dm-header-icons" aria-hidden="true">
        <UIcon name="i-lucide-phone" class="size-[18px]" />
        <UIcon name="i-lucide-video" class="size-[19px]" />
        <UIcon name="i-lucide-pin" class="size-[17px]" />
        <UIcon name="i-lucide-search" class="size-[17px]" />
      </div>
    </div>

    <div class="dc-dm-messages">
      <div class="dc-scroll">
        <slot />
      </div>
    </div>

    <div class="dc-dm-footer" aria-hidden="true">
      <div class="dc-dm-input">
        <UIcon name="i-lucide-circle-plus" class="dc-dm-input-plus size-[22px]" aria-hidden="true" />
        <span class="dc-dm-input-placeholder">{{ placeholder }}</span>
        <div class="dc-dm-input-icons" aria-hidden="true">
          <UIcon name="i-lucide-gift" class="size-[20px]" />
          <UIcon name="i-lucide-image" class="size-[20px]" />
          <UIcon name="i-lucide-smile" class="size-[20px]" />
        </div>
      </div>
      <slot name="typing" />
    </div>
  </div>
</template>

<style scoped>
.dc-dm {
  display: flex;
  flex-direction: column;
  background: var(--dc-bg-chat);
  border: 1px solid var(--dc-hairline);
  border-radius: var(--radius-md);
  overflow: hidden;
  font-family: var(--font-discord);
  box-shadow: var(--shadow-float);
}

.dc-dm-header {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 16px;
  border-bottom: 1px solid var(--dc-hairline);
  flex-shrink: 0;
}

.dc-dm-header-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--dc-ink-header);
}

.dc-dm-header-icons {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  color: var(--dc-ink-channel);
}

.dc-dm-messages {
  flex: 1;
  padding: 12px 0 26px; /* großzügige Luft zwischen letzter Nachricht und Input */
  overflow: hidden;
  position: relative;
}

/* .dc-scroll: kein dauerhaftes will-change — GSAP promotet die Liste nur
   während des Fake-Scroll-Tweens (force3D) */

.dc-dm-footer {
  padding: 0 16px 6px;
  flex-shrink: 0;
}

.dc-dm-input {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--dc-bg-input);
  border: 1px solid var(--dc-border-card);
  border-radius: 24px;
  padding: 10px 16px;
  margin-bottom: 2px;
}

.dc-dm-input-plus {
  color: var(--dc-ink-channel);
  flex-shrink: 0;
}

.dc-dm-input-placeholder {
  flex: 1;
  font-size: 0.95rem;
  color: var(--dc-ink-channel);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dc-dm-input-icons {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--dc-ink-channel);
}
</style>
